import { Search, X } from "lucide-react";
import { useMemo, useEffect, useRef } from "react";
import { matchesCategoryFilter, getMapSections } from "../../utils/filterHelpers";
import { getParentLocations, matchesParentLocation } from "../../utils/locationHelpers";
import type { WebsiteLink } from "../WebsiteGrid";

interface SearchTabProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  selectedSearchTags: string[];
  selectedCategories: string[];
  selectedContinents: string[];
  selectedCountries: string[];
  selectedCities: string[];
  selectedWorldMaps: boolean;
  getSelectedSearchLocation: () => string;
  selectSearchTag: (tag: string) => void;
  allMaps: WebsiteLink[]; // Full maps array for cascading
  onMapClick: (map: WebsiteLink) => void;
}

export function SearchTab({
  searchQuery,
  setSearchQuery,
  selectedSearchTags,
  selectedCategories,
  selectedContinents,
  selectedCountries,
  selectedCities,
  selectedWorldMaps,
  getSelectedSearchLocation,
  selectSearchTag,
  allMaps,
  onMapClick,
}: SearchTabProps) {
  const searchInputRef = useRef<HTMLInputElement>(null);

  const pendingWords = useMemo(() => {
    if (!searchQuery.trim()) return [];
    
    return searchQuery
      .split(' ')
      .map(w => w.trim().toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // Normalize search input too!
        .replace(/[^a-z0-9\s]/g, '')) // Remove special chars like ß
      .filter(w => w.length > 0);
  }, [searchQuery]);

  const cascadedMaps = useMemo(() => {
    const currentLocation = getSelectedSearchLocation();
    const allSelectedTags = selectedSearchTags;

    return allMaps.filter(map => {
      if (selectedCategories.length > 0) {
        if (!matchesCategoryFilter(map, selectedCategories)) {
          return false;
        }
      }

      if (currentLocation) {
        const locationMatch = 
          (currentLocation === "World" && map.world) ||
          (map.continent === currentLocation) ||
          (map.countries?.includes(currentLocation)) ||
          (map.cities?.includes(currentLocation));
        
        // If no direct match and tags are selected, check parent locations
        if (!locationMatch && allSelectedTags.length > 0) {
          const parentLocations = getParentLocations(currentLocation, allMaps);
          const parentMatch = matchesParentLocation(map, parentLocations);
          if (!parentMatch) return false;
        } else if (!locationMatch) {
          return false;
        }
      }

      if (allSelectedTags.length > 0) {
        const allTagsMatch = allSelectedTags.every(tag => 
          map.tags?.includes(tag)
        );
        if (!allTagsMatch) return false;
      }

      return true;
    });
  }, [allMaps, getSelectedSearchLocation, selectedSearchTags, selectedCategories]);

  const cascadedTags = useMemo(() => {
    // Use hierarchical sections to get maps from selected location + all parent levels
    // This ensures tags are shown from city + country + continent + world hierarchy
    const sections = getMapSections(
      allMaps,
      selectedCategories,
      selectedContinents,
      selectedCountries,
      selectedCities,
      selectedWorldMaps,
      selectedSearchTags // Pass selected tags to maintain cascade filtering
    );
    
    // Extract tags from all hierarchical sections
    const allHierarchicalMaps = sections.flatMap(s => s.maps);
    const tags = new Set<string>();
    
    allHierarchicalMaps.forEach(map => {
      if (map.tags) {
        map.tags.forEach(tag => {
          if (!tag.startsWith("last")) {
            tags.add(tag);
          }
        });
      }
    });

    return Array.from(tags).sort((a, b) => a.localeCompare(b));
  }, [allMaps, selectedCategories, selectedContinents, selectedCountries, selectedCities, selectedWorldMaps, selectedSearchTags]);

  // Count maps per tag from cascadedMaps
  const tagMapCounts = useMemo(() => {
    const counts = new Map<string, number>();
    
    cascadedMaps.forEach(map => {
      if (map.tags) {
        map.tags.forEach(tag => {
          if (!tag.startsWith("last")) {
            counts.set(tag, (counts.get(tag) || 0) + 1);
          }
        });
      }
    });
    
    return counts;
  }, [cascadedMaps]);

  // Smart search: auto-select exact tag matches and clear search
  useEffect(() => {
    if (!searchQuery.trim()) {
      return;
    }

    // Split query into words (trim and filter empty)
    const words = searchQuery
      .split(' ')
      .map(w => w.trim())
      .filter(w => w.length > 0);

    // Process each word and check for exact matches
    for (const word of words) {
      const lowerWord = word.toLowerCase();

      // Check for exact tag match (case-insensitive) in cascaded pool
      const exactTagMatches = cascadedTags.filter(
        tag => tag.toLowerCase() === lowerWord
      );

      // Auto-select tag if exactly one match and not already selected
      if (exactTagMatches.length === 1) {
        const matchedTag = exactTagMatches[0];
        if (!selectedSearchTags.includes(matchedTag)) {
          // Ambiguity check: only auto-select if no other tags start with this word
          const hasAmbiguity = cascadedTags.some(
            tag => tag.toLowerCase().startsWith(lowerWord) && 
                   tag.toLowerCase() !== lowerWord
          );
          
          // Only auto-select if unambiguous
          if (!hasAmbiguity) {
            selectSearchTag(matchedTag);
            setSearchQuery(''); // Clear search after auto-selection
            return; // Exit effect after selection
          }
        }
      }
    }
  }, [searchQuery, cascadedTags, selectedSearchTags, selectSearchTag, setSearchQuery]);

  const optimizedTagsToDisplay = useMemo(() => {
    const tagsToShow = new Set<string>();

    // If there are pending words, show partial matches from cascaded pool
    // If no pending words, show FULL cascaded pool
    if (pendingWords.length > 0) {
      cascadedTags.forEach((tag) => {
        // Word-based prefix matching (same logic as project titles)
        const tagWords = tag.toLowerCase()
          .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // Strip accents: ä→a, é→e
          .replace(/[^a-z0-9\s]/g, ' ')  // Replace punctuation with spaces
          .split(/\s+/)                   // Then split on spaces
          .filter(w => w.length > 0);     // Filter empty strings
        
        // Check if any word in the tag starts with any pending search word
        if (pendingWords.some(word => tagWords.some(tagWord => tagWord.startsWith(word)))) {
          tagsToShow.add(tag);
        }
      });

      // Always show selected tags even if they don't match search
      selectedSearchTags.forEach((tag) => tagsToShow.add(tag));
    } else {
      cascadedTags.forEach(tag => tagsToShow.add(tag));
    }

    const selectedTags: string[] = [];
    const unselectedTags: string[] = [];

    tagsToShow.forEach(tag => {
      if (selectedSearchTags.includes(tag)) {
        selectedTags.push(tag);
      } else {
        unselectedTags.push(tag);
      }
    });

    // Keep selected tags in their selection order (chronological)
    const orderedSelectedTags = selectedSearchTags.filter(tag => tagsToShow.has(tag));
    
    unselectedTags.sort((a, b) => a.localeCompare(b));

    return [...orderedSelectedTags, ...unselectedTags];
  }, [cascadedTags, selectedSearchTags, pendingWords]);

  // Title prefix filtering (word-based, respects all active filters)
  const titlePrefixFilteredMaps = useMemo(() => {
    if (!searchQuery.trim()) return []; // Require at least 1 character
    
    const prefix = searchQuery.toLowerCase().trim()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // Normalize search input too!
      .replace(/[^a-z0-9\s]/g, ''); // Remove special chars like ß to match titleWords normalization
    
    // Filter from cascadedMaps (respects all active filters)
    // Match if any word in the title starts with the prefix
    return cascadedMaps.filter(map => {
      const titleWords = map.title.toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // Strip accents: ä→a, é→e
        .replace(/[^a-z0-9\s]/g, ' ')  // Replace punctuation with spaces
        .split(/\s+/)                  // Then split on spaces
        .filter(w => w.length > 0);    // Filter empty strings
      return titleWords.some(word => word.startsWith(prefix));
    });
  }, [searchQuery, cascadedMaps, selectedSearchTags]);

  // Sort alphabetically (A-Z)
  const sortedTitleMaps = useMemo(() => {
    return [...titlePrefixFilteredMaps].sort((a, b) => 
      a.title.localeCompare(b.title)
    );
  }, [titlePrefixFilteredMaps]);

  return (
    <div role="search" aria-label="Search maps by tags or projects" className="space-y-8 pt-6">
      {/* Search Bar */}
      <div className="max-w-2xl mx-auto px-2 md:px-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-white/50" />
          <label htmlFor="map-search" className="sr-only">
            Search for maps by tags
          </label>
          <input
            ref={searchInputRef}
            id="map-search"
            type="text"
            value={searchQuery}
            onChange={(e) =>
              setSearchQuery(e.target.value)
            }
            placeholder="Search for tags or projects..."
            className={`w-full bg-white/5 border border-white/10 rounded-[15px] pl-10 ${searchQuery ? 'pr-16' : 'pr-4'} py-4 text-white placeholder:text-white/70 focus:outline-none focus:border-[var(--accent-blue)] focus:shadow-[0_0_15px_rgba(var(--accent-blue-rgb),0.3)]`}
          />
          {searchQuery && (
            <button
              onClick={() => {
                setSearchQuery('');
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-2.5 text-white/80 hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent rounded"
              aria-label="Clear search"
              type="button"
            >
              <X className="size-5" />
            </button>
          )}
        </div>
      </div>

      {/* Tags Display */}
      <div className={`max-w-2xl mx-auto pt-1 ${sortedTitleMaps.length === 0 ? 'pb-4' : ''}`}>
        {optimizedTagsToDisplay.length > 0 ? (
          <div className="flex flex-wrap gap-2 justify-center">
            {optimizedTagsToDisplay.map((tag) => {
              const isSelected = selectedSearchTags.includes(tag);
              const mapCount = tagMapCounts.get(tag) || 0;
              const showCounter = searchQuery.trim().length >= 1 && mapCount > 1;
              
              return (
                <button
                  key={tag}
                  onClick={() => {
                    selectSearchTag(tag);
                    setSearchQuery('');
                  }}
                  className={`${
                    isSelected
                      ? "bg-[var(--tag-selected-bg)] border-[var(--accent-blue)]"
                      : "bg-white/8 hover:bg-white/10 border-white/10 hover:border-white/20"
                  } rounded-[8px] px-3 py-1 text-[15px] text-white border hover:shadow-[0_0_15px_rgba(var(--accent-blue-rgb),0.3)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent uppercase`}
                  aria-label={`${isSelected ? 'Deselect' : 'Select'} tag: ${tag}`}
                >
                  <span className="flex items-center gap-2">
                    {tag}
                    {showCounter && (
                      <span className="text-green-400 font-regular text-sm">
                        {mapCount}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <p className="text-white/90 text-sm font-medium text-center" role="status" aria-live="polite">
            {sortedTitleMaps.length === 0 
              ? "No tags or projects found!" 
              : "No tags found!"}
          </p>
        )}
      </div>

      {/* Project Title Buttons (Word-Based Prefix Match) */}
      {sortedTitleMaps.length > 0 && (
        <>
          {/* Separator */}
          <div className="max-w-2xl mx-auto">
            <div className="h-[2px] bg-gradient-to-r from-transparent via-white/40 to-transparent" />
          </div>
          
          {/* Title Buttons - Same style as unselected tags */}
          <div className="max-w-2xl mx-auto pb-4">
            <div className="flex flex-wrap gap-2 justify-center">
              {sortedTitleMaps.map((map) => (
                <button
                  key={map.id}
                  onClick={() => onMapClick(map)}
                  className="bg-white/8 hover:bg-white/10 border-white/10 hover:border-white/20 rounded-[8px] px-3 py-1 text-[15px] text-white border hover:shadow-[0_0_15px_rgba(var(--accent-blue-rgb),0.3)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
                  aria-label={`View map: ${map.title}`}
                >
                  {map.title}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}